import { useCallback, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

import { computeRangePreview, orderRange, parseGridcellDay } from "./utils";

type RangeTuple = [Date | null, Date | null];

interface UseRangePickerOptions {
  currentValue: unknown;
  showTime: boolean;
  needConfirm: boolean;
  commitValue: (value: Date | [Date, Date] | null) => void;
  setOpen: (open: boolean) => void;
}

export const useRangePicker = ({
  currentValue,
  showTime,
  needConfirm,
  commitValue,
  setOpen,
}: UseRangePickerOptions) => {
  const [pendingRange, setPendingRange] = useState<RangeTuple | null>(null);
  const [rangeSelectionStep, setRangeSelectionStep] = useState<"from" | "to">(
    "from"
  );
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const committed = (currentValue as RangeTuple | null) ?? null;

  const resetTransientState = useCallback(() => {
    setPendingRange(null);
    setRangeSelectionStep("from");
    setHoveredDate(null);
  }, []);

  const handleRangeSelect = useCallback(
    (_range: { from?: Date; to?: Date } | undefined, triggerDate: Date) => {
      if (!triggerDate) return;

      if (showTime || needConfirm) {
        if (rangeSelectionStep === "from") {
          const existingTo = pendingRange?.[1] ?? committed?.[1] ?? null;
          const nextTo =
            existingTo && triggerDate <= existingTo ? existingTo : null;
          setPendingRange([triggerDate, nextTo]);
        } else {
          const existingFrom = pendingRange?.[0] ?? committed?.[0] ?? null;
          const nextFrom =
            existingFrom && triggerDate >= existingFrom ? existingFrom : null;
          setPendingRange([nextFrom, triggerDate]);
        }

        return;
      }

      if (rangeSelectionStep === "from" || !pendingRange?.[0]) {
        const existingTo = pendingRange?.[1] ?? committed?.[1] ?? null;
        const nextTo =
          existingTo && triggerDate <= existingTo ? existingTo : null;
        setPendingRange([triggerDate, nextTo]);
        setRangeSelectionStep("to");

        return;
      }

      const [from, to] = orderRange(pendingRange[0], triggerDate) as [
        Date,
        Date,
      ];

      setRangeSelectionStep("from");
      setPendingRange(null);

      commitValue([from, to]);
      setOpen(false);
    },
    [
      rangeSelectionStep,
      pendingRange,
      committed,
      showTime,
      needConfirm,
      commitValue,
      setOpen,
    ]
  );

  const handleHoverIn = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const parsed = parseGridcellDay(e.target);
      if (!parsed) return;

      if (hoveredDate && hoveredDate.getTime() === parsed.getTime()) return;

      setHoveredDate(parsed);
    },
    [hoveredDate]
  );

  const handleHoverOut = useCallback(() => setHoveredDate(null), []);

  const memoizedRangeSelected = useMemo<DateRange | undefined>(() => {
    const { from, to } = computeRangePreview({
      pendingRange,
      committed,
      hoveredDate,
      rangeStep: rangeSelectionStep,
    });

    if (!from && !to) return undefined;

    return { from: from ?? undefined, to: to ?? undefined };
  }, [pendingRange, committed, hoveredDate, rangeSelectionStep]);

  const getPreviewParts = useCallback(
    () =>
      computeRangePreview({
        pendingRange,
        committed,
        hoveredDate,
        rangeStep: rangeSelectionStep,
      }),
    [pendingRange, committed, hoveredDate, rangeSelectionStep]
  );

  return {
    pendingRange,
    rangeSelectionStep,
    setRangeSelectionStep,
    hoveredDate,
    handleRangeSelect,
    handleHoverIn,
    handleHoverOut,
    memoizedRangeSelected,
    getPreviewParts,
    resetTransientState,
  };
};
